'use client';

import {
	MAX_STEPS_FOR_NAVIGATION,
	type StepperRef,
	useIntl,
	useSetPageTopBarHeaderLeft,
	useStepper
} from '@capsicum/common';
import { forwardRef, useCallback, useImperativeHandle } from 'react';
import styled from 'styled-components';

import { useStepperConfirmationModal } from '../../../hooks/helpers/useStepperConfirmationModal';
import { useRouter } from '../../../hooks/router/useCustomRouter';
import { InteractionLayer } from '../../atoms';
import { Button } from '../../atoms/buttons/button/Button';
import { CheckBolderIcon } from '../../icons';
import { BackButton } from '../back-button/BackButton';
import { ButtonSection } from '../button-section/ButtonSection';

export type StepperProps = {
	allowToGoToPreviousSteps?: boolean;
	customBackNavigation?: () => void;
	disabledFirstButton?: boolean;
	disabledSecondButton?: boolean;
	disableStepsNavigationOnStep?: Array<number>;
	firstButtonText?: string;
	fullBorderRadius?: boolean;
	fullscreen?: boolean;
	hasStickyButtons?: boolean;
	hiddenFirstButton?: boolean;
	hiddenSecondButton?: boolean;
	hideActionButtons?: boolean;
	hideStepsNavigationOnSteps?: Array<number>;
	isLoading?: boolean;
	onBackNavigationClick?: () => void;
	onFirstButtonClick?: (proceedToNextStep: () => void, currentIndex: number) => void;
	onlyButtonsNavigation?: boolean;
	onSecondButtonClick?: () => void;
	onStepIndexChange?: (index: number) => void;
	onSubmitSteps: () => void;
	prerenderSteps?: boolean;
	showExitAlertConfirmation?: boolean;
	showStepsNavigation?: boolean;
	steps: Array<() => JSX.Element>;
	testID: string;
};

/**
 * Stepper component.
 *
 * @description If it is necessary to pass different texts/callbacks for buttons for different steps,
 * need to use the onStepIndexChange method to get the current step index and, depending on the index,
 * send certain texts/callbacks for buttons
 *
 * @param {boolean} props.allowToGoToPreviousSteps - If true, allow the user to go to previous steps
 * @param {boolean} props.customBackNavigation - Function to exit the stepper with a custom navigation
 * @param {boolean} props.disabledFirstButton - If true, disable all interactions for the first button
 * @param {boolean} props.disabledSecondButton - If true, disable all interactions for the second button
 * @param {boolean} props.disableStepsNavigationOnStep - If true, disable all interactions for the steps navigation on specific step
 * @param {string} props.firstButtonText - Text for the first button
 * @param {boolean} props.fullscreen - If true, Stepper takes all height and makes content scrollable if needed
 * @param {boolean} props.fullBorderRadius - If true, the step container will be fully rounded (if we hide the buttons)
 * @param {boolean} props.hideActionButtons - If true, hide the action buttons
 * @param {boolean} props.hiddenFirstButton - If true, hide the first button
 * @param {boolean} props.hiddenSecondButton - If true, hide the second button
 * @param {Array<number>} props.hideStepsNavigationOnSteps - Array of step indexes we want to hide
 * @param {boolean} props.isLoading - If true, give the loading state to the button
 * @param {() => void} props.onBackNavigationClick - The callback function to be called on back navigation click
 * @param {() => void} props.onFirstButtonClick - The callback function to be called on the first button click
 * @param {boolean} props.onlyButtonsNavigation - If true, changes the buttons behavior to only navigate between steps
 * @param {() => void} props.onSecondButtonClick - The callback function to be called on the second button click
 * @param {() => void} props.onSubmitSteps - The callback function to be called when all steps are completed
 * @param {(index: number) => void} props.onStepIndexChange - Callback that is called when the step index changes
 * @param {boolean} props.prerenderSteps - If true, render all steps when Stepper gets created. Otherwise, renders the steps only when they get visible. Default is true.
 * @param {boolean} props.showStepsNavigation - If true, show the steps navigation
 * @param {Array<() => JSX.Element>} props.steps - List of components to render
 * @param {string} props.testID - TestID
 * @returns {JSX.Element} The Stepper component
 */
export const Stepper = forwardRef<StepperRef, StepperProps>(
	(
		{
			allowToGoToPreviousSteps = true,
			customBackNavigation,
			disabledFirstButton,
			disabledSecondButton,
			disableStepsNavigationOnStep,
			firstButtonText,
			fullBorderRadius,
			fullscreen,
			hasStickyButtons = false,
			hiddenFirstButton,
			hiddenSecondButton,
			hideActionButtons,
			hideStepsNavigationOnSteps,
			isLoading,
			onBackNavigationClick,
			onFirstButtonClick,
			onlyButtonsNavigation,
			onSecondButtonClick,
			onStepIndexChange,
			onSubmitSteps,
			prerenderSteps = true,
			showExitAlertConfirmation = true,
			showStepsNavigation = false,
			steps,
			testID,
			...props
		},
		ref
	) => {
		const { currentStepIndex, hasPassedFirstStep, proceedToNextStep, proceedToPrevStep, setStepIndex } = useStepper(
			{
				onStepIndexChange,
				stepsLength: steps.length
			}
		);
		const { $t } = useIntl();
		const { back } = useRouter();

		const isLastStep = currentStepIndex === steps.length - 1;
		const hasPreviousStep = currentStepIndex !== 0;

		const hideStepsNavigationForCurrentIndex = hideStepsNavigationOnSteps?.includes(currentStepIndex + 1);
		const disableStepsNavigationForCurrentIndex = disableStepsNavigationOnStep?.includes(currentStepIndex + 1);

		const showStepsNavigationContainer =
			!hideStepsNavigationForCurrentIndex && showStepsNavigation && steps.length < MAX_STEPS_FOR_NAVIGATION;

		const { openExitConfirmationModal } = useStepperConfirmationModal();

		const onFirstButtonClickHandler = useCallback(() => {
			if (onFirstButtonClick) {
				onFirstButtonClick(proceedToNextStep, currentStepIndex);
			} else {
				proceedToNextStep();
			}
		}, [currentStepIndex, onFirstButtonClick, proceedToNextStep]);

		const exitStepper = useCallback(() => {
			if (hasPassedFirstStep && !onlyButtonsNavigation && showExitAlertConfirmation) {
				openExitConfirmationModal({
					onConfirmation: customBackNavigation,
					testID
				});

				return;
			}

			if (customBackNavigation) {
				customBackNavigation();

				return;
			}
			back();
		}, [hasPassedFirstStep, customBackNavigation]);

		const onCancelClickHandler = () => {
			if (onlyButtonsNavigation && hasPreviousStep && allowToGoToPreviousSteps) {
				proceedToPrevStep();

				return;
			}
			if (onSecondButtonClick) {
				onSecondButtonClick();

				return;
			}
			exitStepper();
		};

		const onBackButtonHandler = useCallback(() => {
			if (onBackNavigationClick) {
				onBackNavigationClick();

				return;
			}

			if (hasPreviousStep && allowToGoToPreviousSteps) {
				proceedToPrevStep();
			} else exitStepper();
		}, [allowToGoToPreviousSteps, hasPreviousStep, onBackNavigationClick]);

		const onLastStepSubmitHandler = useCallback(() => {
			onSubmitSteps();
		}, [onSubmitSteps]);

		// Provide an method Stepper for outside.
		useImperativeHandle(
			ref,
			() => ({
				goToNextStep: () => {
					proceedToNextStep();
				},
				goToPrevStep: () => {
					if (!allowToGoToPreviousSteps) {
						return;
					}
					proceedToPrevStep();
				},
				moveToStepIndex: (index: number) => {
					if (!allowToGoToPreviousSteps) {
						return;
					}
					setStepIndex(index);
				}
			}),
			[proceedToNextStep, proceedToPrevStep, setStepIndex]
		);

		const goToStep = useCallback(
			(index: number) => {
				if (!allowToGoToPreviousSteps) {
					return;
				}
				setStepIndex(index);
			},
			[allowToGoToPreviousSteps, setStepIndex]
		);

		useSetPageTopBarHeaderLeft(<BackButton testID={`${testID}-back-button`} onClick={onBackButtonHandler} />);

		const renderStepsNavigation = useCallback(() => {
			const stepsLength = steps.length;

			return Array.from({ length: stepsLength }, (_, index) => (
				<S.ContainerInteractionLayer
					disabled={index > currentStepIndex || disableStepsNavigationForCurrentIndex}
					key={index}
					onClick={() => goToStep(index)}
				>
					<S.StepNavigation $disabled={index > currentStepIndex} $isValidated={index < currentStepIndex}>
						{index < currentStepIndex ? <CheckBolderIcon height={12} width={12} /> : index + 1}
					</S.StepNavigation>
				</S.ContainerInteractionLayer>
			));
		}, [currentStepIndex, steps.length, disableStepsNavigationForCurrentIndex]);

		const renderSteps = useCallback(() => {
			return steps.map((step, index) =>
				prerenderSteps || currentStepIndex === index ? (
					<S.StepWrapper
						$fullscreen={!!fullscreen}
						$increasePaddingTop={showStepsNavigationContainer}
						$isVisible={currentStepIndex === index}
						key={index}
					>
						{step()}
					</S.StepWrapper>
				) : null
			);
		}, [currentStepIndex, steps]);

		return (
			<div data-testid={testID}>
				<S.Content $fullBorderRadius={Boolean(fullBorderRadius)} {...props}>
					{showStepsNavigationContainer && (
						<S.StepsNavigation $stepsLength={steps.length} className="dsjkadlasjdlakj">
							{renderStepsNavigation()}
						</S.StepsNavigation>
					)}
					{renderSteps()}
				</S.Content>
				{hasStickyButtons ? (
					<S.StickyButtonWrapper>
						<Button
							testID={`${testID}-button-continue`}
							text={
								firstButtonText ||
								(isLastStep ? $t('global.button.letsGetStarted') : $t('global.button.continue'))
							}
							onClick={isLastStep ? onLastStepSubmitHandler : onFirstButtonClickHandler}
						/>
					</S.StickyButtonWrapper>
				) : (
					!hideActionButtons && (
						<S.ButtonsWrapper $increasePaddingTop={showStepsNavigation}>
							{!hiddenSecondButton && (
								<Button
									disabled={disabledSecondButton}
									testID={`${testID}-button-second`}
									text={$t(
										`global.button.${onlyButtonsNavigation && hasPreviousStep ? 'back' : 'cancel'}`
									)}
									variant="outlined"
									onClick={onCancelClickHandler}
								/>
							)}
							{!hiddenFirstButton && (
								<Button
									disabled={disabledFirstButton}
									isLoading={isLoading}
									testID={`${testID}-button-first`}
									text={
										firstButtonText ||
										(isLastStep ? $t('global.button.submit') : $t('global.button.continue'))
									}
									onClick={isLastStep ? onLastStepSubmitHandler : onFirstButtonClickHandler}
								/>
							)}
						</S.ButtonsWrapper>
					)
				)}
			</div>
		);
	}
);

Stepper.displayName = 'Stepper';

namespace S {
	export const ContainerInteractionLayer = styled(InteractionLayer)`
		${({ theme }) => theme.border.radius.circle};
	`;

	export const StepsNavigation = styled.div<{ $stepsLength: number }>`
		position: absolute;
		left: 50%;
		transform: translate(-50%, -50%);
		display: flex;
		gap: ${({ $stepsLength, theme }) => ($stepsLength > 4 ? theme.spacings.medium : theme.spacings.xxlarge)};
		justify-content: space-between;
		align-items: center;
	`;

	export const StepNavigation = styled.span<{ $disabled: boolean; $isValidated: boolean }>`
		cursor: pointer;
		${({ theme }) => theme.fonts.text.caption1};
		${({ theme }) => theme.border.radius.circle};
		border-style: solid;
		border-color: ${({ theme }) => theme.colors.borders.brand.enabled};
		border-width: ${({ theme }) => theme.border.width.small};
		padding: ${({ $isValidated, theme }) => ($isValidated ? '5px' : `4px ${theme.sizing.xxxsmall}`)};
		background-color: ${({ $disabled, $isValidated, theme }) =>
			$disabled || $isValidated
				? theme.colors.backgrounds.brand.enabled
				: theme.colors.backgrounds.main.container};

		color: ${({ $disabled, $isValidated, theme }) =>
			$disabled || $isValidated ? theme.colors.text.main.inverse : theme.colors.text.brand.enabled};
	`;

	export const Content = styled.div<{ $fullBorderRadius: boolean }>`
		border-radius: ${({ $fullBorderRadius, theme }) =>
			$fullBorderRadius ? `${theme.sizing.xxxsmall}` : `${theme.sizing.xxxsmall} ${theme.sizing.xxxsmall} 0 0`};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
	`;

	export const StepWrapper = styled.div<{ $fullscreen: boolean; $increasePaddingTop: boolean; $isVisible: boolean }>`
		display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
		overflow-y: ${({ $fullscreen }) => ($fullscreen ? 'scroll' : 'visible')};
		padding: ${({ $fullscreen, $increasePaddingTop, theme }) =>
			$fullscreen
				? 0
				: `${$increasePaddingTop ? theme.spacings.xlarge : theme.spacings.large} ${theme.spacings.small} ${theme.spacings.large} ${theme.spacings.small}`};
	`;

	export const ButtonsWrapper = styled.div<{ $increasePaddingTop: boolean }>`
		display: flex;
		justify-content: flex-end;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		padding: ${({ theme }) => theme.spacings.small};
		padding-bottom: ${({ theme }) => theme.spacings.large};
		border-radius: ${({ theme }) => `0 0 ${theme.sizing.xxxsmall} ${theme.sizing.xxxsmall}`};
	`;
	export const StickyButtonWrapper = styled(ButtonSection)`
		${({ theme }) => theme.breakpoints.queries.above.tabletSmallDesktop} {
			margin-top: 0;
			padding-bottom: ${({ theme }) => theme.spacings.xxlarge};
		}
	`;
}
